import { isTesting } from 'shared/utils/isTesting'
import type { WallpaperSeason } from 'shared/wallpapers/animatedWallpaper.types'
import {
  getAnimatedWallpaper,
  setBackendAnimatedWallpapers
} from 'shared/wallpapers/catalog'
import { resolveAnimatedWallpaperScene } from 'shared/wallpapers/resolveAnimatedWallpaperScene'
import { create } from 'zustand'

import { PredefinedWallpaper } from '../api/containerApi/requests/getAllDesktopSettings/getAllDesktopSettings.types'
import { WallpaperDataType } from '../types'

const convertPredefinedWallpapersToWallpaperDataType = (
  predefinedWallpapers: PredefinedWallpaper[]
): WallpaperDataType[] => {
  return predefinedWallpapers.map(wallpaper => {
    const definition = getAnimatedWallpaper(wallpaper.wallpaper_id)
    const scene = definition
      ? resolveAnimatedWallpaperScene(definition)
      : undefined
    return {
      id: wallpaper.wallpaper_id,
      image:
        scene && definition
          ? `${definition.assetBaseUrl}${scene.poster}`
          : wallpaper.url,
      thumbnail:
        scene && definition
          ? `${definition.assetBaseUrl}${scene.thumbnail}`
          : wallpaper.thumbnail_url,
      name: wallpaper.name,
      localizedNames: definition?.localizedNames,
      animation: definition?.id
    }
  })
}

export const useWallpaperStore = create<{
  currentWallpaper: WallpaperDataType
  seasonPreview: WallpaperSeason | null
  setSeasonPreview: (season: WallpaperSeason | null) => void
  predefinedWallpapers: WallpaperDataType[]
  customWallpapers: WallpaperDataType[]
  setCurrentWallpaper: (wallpaper: WallpaperDataType) => void
  updateCurrentWallpaperAssets: (
    id: string,
    image: string,
    thumbnail: string | undefined
  ) => void
  setPredefinedWallpapers: (predefinedWallpapers: PredefinedWallpaper[]) => void
  setCustomWallpapers: (customWallpapers: WallpaperDataType[]) => void
  addCustomWallpaper: (wallpaper: WallpaperDataType) => void
  removeCustomWallpaper: (id: string | number) => void
}>()(set => ({
  currentWallpaper: { id: '', image: '' },
  seasonPreview: null,
  setSeasonPreview: seasonPreview => {
    // Preview controls must never override calendar seasons in production.
    if (!isTesting()) return
    set({ seasonPreview })
  },
  predefinedWallpapers: [],
  customWallpapers: [],
  setCurrentWallpaper: wallpaper =>
    set(state => ({
      currentWallpaper: wallpaper,
      seasonPreview:
        state.currentWallpaper.id === wallpaper.id ? state.seasonPreview : null
    })),
  updateCurrentWallpaperAssets: (id, image, thumbnail) =>
    set(state => {
      const current = state.currentWallpaper
      if (
        current.id !== id ||
        (current.image === image && current.thumbnail === thumbnail)
      )
        return state
      // Updating the selected scene's assets preserves its preview and identity.
      return { currentWallpaper: { ...current, image, thumbnail } }
    }),
  setPredefinedWallpapers: predefinedWallpapers => {
    setBackendAnimatedWallpapers(predefinedWallpapers)
    set({
      predefinedWallpapers:
        convertPredefinedWallpapersToWallpaperDataType(predefinedWallpapers)
    })
  },
  setCustomWallpapers: customWallpapers => set({ customWallpapers }),
  addCustomWallpaper: wallpaper =>
    set(state => ({
      customWallpapers: [...state.customWallpapers, wallpaper]
    })),
  removeCustomWallpaper: id =>
    set(state => ({
      customWallpapers: state.customWallpapers.filter(
        wallpaper => wallpaper.id !== id
      )
    }))
}))
